'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import SiteNav from '@/components/SiteNav';

import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

export default function LoginPage() {
  const router = useRouter();
  const { signIn, signUp } = useAuth();

  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');
    setLoading(true);

    try {
      if (mode === 'login') {
        await signIn(email, password);
        router.push('/dashboard');
      } else {
        await signUp(email, password);
        setSuccessMsg('Check your inbox — we sent you a confirmation link.');
      }
    } catch {
      setError("That email or password doesn't look right. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError('Please enter your email address above, then click "Forgot your password?"');
      return;
    }
    setResetLoading(true);
    setError('');
    try {
      const supabase = createClient();
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${process.env.NEXT_PUBLIC_SITE_URL}/reset-password`,
      });
      if (resetError) throw resetError;
      setResetSent(true);
      setSuccessMsg('Password reset email sent — check your inbox.');
    } catch {
      setError('Could not send reset email. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#1B2A4A' }}>
      <SiteNav currentPath="/login" />

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          {/* Card */}
          <div className="card-warm p-8 sm:p-10">
            {/* Heading */}
            <div className="mb-8 text-center">
              <h1
                className="font-display text-3xl font-semibold mb-2"
                style={{ color: 'var(--navy)' }}
              >
                {mode === 'login' ? 'Welcome back.' : 'Create your account'}
              </h1>
              <p className="font-body text-sm" style={{ color: 'var(--text-muted)' }}>
                {mode === 'login' ? 'Sign in to access your modules and downloads.' : 'Join That Clicked and start learning today.'}
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="flex flex-col gap-5">
              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor="email"
                  className="font-body text-sm font-medium"
                  style={{ color: 'var(--navy)' }}
                >
                  Email address
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full font-body text-sm px-4 py-3 rounded-lg border outline-none transition-all duration-150 focus:ring-2"
                  style={{
                    borderColor: 'var(--border)',
                    color: 'var(--text-primary)',
                    backgroundColor: 'white',
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = 'var(--amber)';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(212,137,26,0.15)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = 'var(--border)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor="password"
                  className="font-body text-sm font-medium"
                  style={{ color: 'var(--navy)' }}
                >
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={mode === 'signup' ? 'At least 6 characters' : '••••••••'}
                  className="w-full font-body text-sm px-4 py-3 rounded-lg border outline-none transition-all duration-150"
                  style={{
                    borderColor: 'var(--border)',
                    color: 'var(--text-primary)',
                    backgroundColor: 'white',
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = 'var(--amber)';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(212,137,26,0.15)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = 'var(--border)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                />
                {/* Forgot password link */}
                {mode === 'login' && (
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    disabled={resetLoading || resetSent}
                    className="font-body text-xs text-left transition-colors duration-150 hover:underline disabled:opacity-60 disabled:cursor-not-allowed"
                    style={{ color: 'var(--text-muted)' }}
                  >
                    {resetLoading ? 'Sending…' : 'Forgot your password?'}
                  </button>
                )}
              </div>

              {/* Error message */}
              {error && (
                <div
                  className="font-body text-sm text-center px-4 py-3 rounded-lg"
                  style={{
                    backgroundColor: '#FEF2F2',
                    color: '#B91C1C',
                    border: '1px solid #FECACA',
                  }}
                >
                  {error}
                </div>
              )}

              {/* Success message */}
              {successMsg && (
                <div
                  className="font-body text-sm px-4 py-3 rounded-lg"
                  style={{
                    backgroundColor: '#F0FDF4',
                    color: '#15803D',
                    border: '1px solid #BBF7D0',
                  }}
                >
                  {successMsg}
                </div>
              )}

              {/* Submit button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full font-body text-sm font-semibold py-3.5 rounded-lg transition-all duration-200 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed mt-1"
                style={{ backgroundColor: 'var(--amber)', color: 'white' }}
                onMouseEnter={(e) => {
                  if (!loading) e.currentTarget.style.backgroundColor = 'var(--amber-light)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--amber)';
                }}
              >
                {loading
                  ? mode === 'login' ? 'Signing in…' : 'Creating account…'
                  : mode === 'login' ? 'Sign in' : 'Create account'}
              </button>
            </form>

            {/* Mode toggle */}
            <p
              className="font-body text-sm text-center mt-6"
              style={{ color: 'var(--text-muted)' }}
            >
              {mode === 'login' ? (
                <>
                  {"Don't have an account yet? "}
                  <Link
                    href="/landing-page"
                    className="font-semibold transition-colors duration-150 hover:underline"
                    style={{ color: 'var(--amber)' }}
                  >
                    Start free →
                  </Link>
                </>
              ) : (
                <>
                  {'Already have an account? '}
                  <button
                    type="button"
                    onClick={() => {
                      setMode('login');
                      setError('');
                      setSuccessMsg('');
                    }}
                    className="font-semibold transition-colors duration-150 hover:underline"
                    style={{ color: 'var(--amber)' }}
                  >
                    Sign in
                  </button>
                </>
              )}
            </p>
          </div>

          {/* Back link */}
          <p className="font-body text-sm text-center mt-6" style={{ color: 'rgba(255,255,255,0.6)' }}>
            <Link
              href="/landing-page"
              className="transition-colors duration-150 hover:underline"
              style={{ color: 'rgba(255,255,255,0.7)' }}
            >
              ← Back to That Clicked
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
}
